import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Pages } from "../../utils/route-paths";
import { useGetProjectFileQuery } from "../../hooks/use-get-project-file-query";
import DashboardHeader from "../../components/dashboard-header";
import { ProjectFilePreview, ProjectFilePreviewData } from "../../components/project-file-preview";
import FloatingChatWidget from "../../components/floating-chat-widget";

type ProjectDetailsState = {
  loading: boolean;
  error?: string | null;
  project?: ProjectFilePreviewData | null;
};

export const DesignerProjectDetailsPage = () => {
  const { designerId, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getProjectFile } = useGetProjectFileQuery();
  const locationState = (location.state as {
    backTo?: string;
    designerName?: string;
  } | null);
  const backTo = locationState?.backTo;
  const chatOpenRef = useRef<(() => void) | null>(null);
  const pendingOpenChatRef = useRef(false);

  const [state, setState] = useState<ProjectDetailsState>({ loading: true });

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setState({
          loading: false,
          error: "We couldn't find that project.",
        });
        return;
      }

      try {
        setState((prev) => ({ ...prev, loading: true }));
        const project = await getProjectFile(projectId, true);
        if (!project) {
          setState({
            loading: false,
            error: "We couldn't find that project.",
          });
          return;
        }
        setState({
          loading: false,
          project,
        });
      } catch (error) {
        console.error("Error loading project details:", error);
        setState({
          loading: false,
          error: "Something went wrong loading this project. Please try again.",
        });
      }
    };

    fetchProject();
  }, [projectId, getProjectFile]);

  const project = state.project;

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
      return;
    }
    if (designerId) {
      navigate(Pages.DESIGNER_PREMIUM_PROFILE.replace(":designerId", designerId));
      return;
    }
    navigate(-1);
  };

  const handleConnectWithDesigner = () => {
    if (chatOpenRef.current) {
      chatOpenRef.current();
      return;
    }
    pendingOpenChatRef.current = true;
  };

  return (
    <Box className="site-wrapper">
      <DashboardHeader 
        title="Project Details"
        showBackButton={true}
        onBack={handleBack}
      />
      <Stack spacing={{ xs: 3, md: 4 }}>
        {state.loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress />
          </Box>
        ) : state.error ? (
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 3 },
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {state.error}
            </Typography>
          </Box>
        ) : project ? (
          <ProjectFilePreview
            project={project}
            designerId={designerId || null}
            showConnectButton={true}
            onConnectWithDesigner={handleConnectWithDesigner}
          />
        ) : null}
      </Stack>
      {designerId && (
        <FloatingChatWidget
          recipientId={designerId}
          recipientRole="Designer"
          recipientName={locationState?.designerName || "Designer"}
          renderTrigger={() => null}
          onReady={(openChat) => {
            chatOpenRef.current = openChat;
            if (pendingOpenChatRef.current) {
              pendingOpenChatRef.current = false;
              openChat();
            }
          }}
        />
      )}
    </Box>
  );
};
